import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { useRobot } from "../hooks/useRobot";
import { useRobotState } from "../hooks/useRobotState";
import type { RobotViewer } from "../modules/robot-viewer";
import type { PlannerMode } from "../modules/teleop-state";
import {
  LIFT_CALIBRATION_KEY,
  readLiftCalibration,
} from "../modules/robot-viewer/lift-calibration";
import { LiftCalibrationPanel } from "./LiftCalibrationPanel";
import styles from "./RobotModelPanel.module.scss";

/** joint-state comes at ≈30 Hz — older than this and the model is shown as stale. */
const STALE_MS = 1000;
const COLLAPSED_KEY = "mmr.robotModel.collapsed";

const MODE_LABEL: Record<PlannerMode, string> = {
  hold: "Удержание",
  manual: "Ручное",
  goal: "К цели",
};

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function themeColors(): { background: string; grid: string } {
  const css = getComputedStyle(document.documentElement);
  return {
    background: css.getPropertyValue("--color-surface-inset").trim() || "#1e1e1e",
    grid: css.getPropertyValue("--color-border").trim() || "#555555",
  };
}

/**
 * 3D model of the robot (URDF from robot-config.robot.description_url) with the measured
 * joint positions from the planner's `joint-state`. Display only; the camera can be
 * orbited with the mouse. Sits left of the camera view.
 */
export function RobotModelPanel() {
  const robotState = useRobotState();
  const { config } = useRobot();
  const urdfUrl = config?.robot?.description_url ?? null;
  const isPanther = config?.robot?.model === "panther";
  const [liftCalibration, setLiftCalibration] = useState(readLiftCalibration);
  const liftCalibrationRef = useRef(liftCalibration);
  liftCalibrationRef.current = liftCalibration;
  const mode = useStore(robotState.armState, (s) => s.joints?.mode ?? null);
  const hasJoints = useStore(robotState.armState, (s) => s.joints !== null);

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [stale, setStale] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const syncCalibration = (event: StorageEvent) => {
      if (
        event.storageArea === localStorage &&
        (event.key === LIFT_CALIBRATION_KEY || event.key === null)
      ) {
        setLiftCalibration(readLiftCalibration());
      }
    };
    window.addEventListener("storage", syncCalibration);
    return () => window.removeEventListener("storage", syncCalibration);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* storage unavailable — the panel just forgets its state */
    }
  }, [collapsed]);

  // Staleness: re-evaluated on a timer (joint-state may simply stop arriving).
  useEffect(() => {
    const id = setInterval(() => {
      const at = robotState.armState.getState().receivedAt;
      setStale(at === null || Date.now() - at > STALE_MS);
    }, 250);
    return () => clearInterval(id);
  }, [robotState]);

  // The viewer lives outside React: joint updates go straight from the store to three.js.
  const viewerRef = useRef<RobotViewer | null>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (collapsed || !urdfUrl || !container) return undefined;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    let themeObserver: MutationObserver | null = null;
    setLoadState("loading");

    void import("../modules/robot-viewer")
      .then(({ createRobotViewer }) => {
        if (cancelled) return;
        const colors = themeColors();
        const viewer = createRobotViewer(container, {
          urdfUrl,
          background: colors.background,
          gridColor: colors.grid,
        });
        viewerRef.current = viewer;
        viewer.setLiftCalibration(isPanther ? liftCalibrationRef.current : null);
        const apply = () => {
          const joints = robotState.armState.getState().joints;
          if (joints) {
            viewer.setJoints(joints.names, joints.position);
            if (joints.base_pose) viewer.setBasePose(joints.base_pose);
          }
        };
        unsubscribe = robotState.armState.subscribe(apply);
        themeObserver = new MutationObserver(() => {
          const c = themeColors();
          viewer.setColors(c.background, c.grid);
        });
        themeObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ["data-theme"],
        });
        viewer.ready.then(
          () => {
            if (cancelled) return;
            apply();
            setLoadState("ready");
          },
          (err: unknown) => {
            if (cancelled) return;
            console.warn("[robot-viewer] model load failed", err);
            setLoadState("error");
          }
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn("[robot-viewer] module load failed", err);
        setLoadState("error");
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
      themeObserver?.disconnect();
      viewerRef.current?.dispose();
      viewerRef.current = null;
    };
  }, [collapsed, urdfUrl, robotState, isPanther]);

  useEffect(() => {
    viewerRef.current?.setLiftCalibration(isPanther ? liftCalibration : null);
  }, [isPanther, liftCalibration, loadState]);

  useEffect(() => {
    viewerRef.current?.setStale(stale);
  }, [stale, loadState]);

  if (collapsed) {
    return (
      <aside
        className={`${styles.panel} ${styles.collapsed}`}
        aria-label="Модель робота (свёрнута)"
      >
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => setCollapsed(false)}
          title="Показать модель робота"
          aria-label="Показать модель робота"
        >
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
        <span className={styles.verticalTitle}>Модель</span>
      </aside>
    );
  }

  let overlay: string | null = null;
  if (!urdfUrl) overlay = "Планировщик не сообщил модель робота";
  else if (loadState === "loading") overlay = "Загрузка модели…";
  else if (loadState === "error") overlay = "Не удалось загрузить модель";
  else if (!hasJoints) overlay = "Нет данных о суставах";

  return (
    <aside className={styles.panel} aria-label="Модель робота">
      <header className={styles.header}>
        <span className={styles.title}>Модель робота</span>
        {mode && !stale && <span className={styles.chip}>{MODE_LABEL[mode]}</span>}
        {hasJoints && stale && <span className={`${styles.chip} ${styles.warn}`}>нет данных</span>}
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => setCollapsed(true)}
          title="Свернуть"
          aria-label="Свернуть модель робота"
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
        </button>
      </header>
      <div className={styles.canvas}>
        {/* three.js owns this node's children; React never renders into it. */}
        <div className={styles.viewport} ref={containerRef} />
        {overlay && <div className={styles.overlay}>{overlay}</div>}
      </div>
      {isPanther && (
        <LiftCalibrationPanel calibration={liftCalibration} onChange={setLiftCalibration} />
      )}
    </aside>
  );
}
